import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, ChevronLeft, UserPlus, Users, FileCheck, CheckCircle, Loader2, AlertCircle, Info, BookOpen, ExternalLink } from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Alert, AlertDescription } from '../ui/alert';

import { PRICE_LIST_PDF_URL } from '@/config/cdn';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MemberRegistrationForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Check if this is "existing customer" mode (adding student to existing account)
  const mode = searchParams.get('mode'); // 'new' or 'existing'
  const isExistingCustomer = mode === 'existing';

  // Resource link reference (from resource link deep links)
  const ref = searchParams.get('ref');
  const [resourceLinkInfo, setResourceLinkInfo] = useState(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(null); // { tempToken, resourceTitle }

  // Form state
  const [formData, setFormData] = useState({
    // Parent info
    parent_name: '',
    parent_email: '',
    parent_phone: '',
    parent_address: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    account_type: 'parent',

    // Students
    students: [
      {
        name: '',
        date_of_birth: '',
        email: '',
        current_belt: 'white',
        program_ids: [],
        username: '',
        password: '',
        confirm_password: '',
        enable_password: false,
      }
    ],

    // Programs
    selected_program_ids: [],
    payment_method_preference: '',
    quickstart_interest: false,

    // Waivers
    liability_waiver_accepted: false,
    photo_video_consent: '',
    photo_video_caption_preference: '',
    digital_signature: '',
    coppa_consent_provided: false,  // COPPA parental consent for minors
  });

  // Programs (will be loaded from API)
  const [programs, setPrograms] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(true);

  // Load programs on mount
  useEffect(() => {
    const loadPrograms = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/programs`);
        if (response.ok) {
          const data = await response.json();
          setPrograms(data);
        }
      } catch (err) {
        console.error('Failed to load programs:', err);
      } finally {
        setLoadingPrograms(false);
      }
    };
    loadPrograms();
  }, []);

  // Fetch resource link metadata if ref param is present
  useEffect(() => {
    if (!ref) return;
    const fetchResourceLink = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/public/resource-links/${ref}`);
        if (response.ok) {
          const data = await response.json();
          setResourceLinkInfo(data);
        }
      } catch (err) {
        console.error('Failed to fetch resource link info:', err);
      }
    };
    fetchResourceLink();
  }, [ref]);

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateStudent = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      students: prev.students.map((s, i) =>
        i === index ? { ...s, [field]: value } : s
      )
    }));
  };

  const addStudent = () => {
    setFormData(prev => ({
      ...prev,
      students: [
        ...prev.students,
        {
          name: '',
          date_of_birth: '',
          email: '',
          current_belt: 'white',
          program_ids: [],
          username: '',
          password: '',
          confirm_password: '',
          enable_password: false,
        }
      ]
    }));
  };

  const removeStudent = (index) => {
    if (formData.students.length === 1) return;
    setFormData(prev => ({
      ...prev,
      students: prev.students.filter((_, i) => i !== index)
    }));
  };

  const validateStep = () => {
    setError(null);

    if (step === 1) {
      // For existing customers, only validate email
      if (isExistingCustomer) {
        if (!formData.parent_email.trim()) {
          setError('Email is required');
          return false;
        }
        if (!/\S+@\S+\.\S+/.test(formData.parent_email)) {
          setError('Please enter a valid email address');
          return false;
        }
      } else {
        // For new customers, validate all fields
        if (!formData.parent_name.trim()) {
          setError('Parent/Guardian name is required');
          return false;
        }
        if (!formData.parent_email.trim()) {
          setError('Email is required');
          return false;
        }
        if (!/\S+@\S+\.\S+/.test(formData.parent_email)) {
          setError('Please enter a valid email address');
          return false;
        }
      }
    }

    if (step === 2) {
      for (let i = 0; i < formData.students.length; i++) {
        const student = formData.students[i];
        if (!student.name.trim()) {
          setError(`Student ${i + 1}: Name is required`);
          return false;
        }
        if (student.enable_password) {
          if (!student.date_of_birth) {
            setError(`Student ${i + 1}: Date of birth is required for password login`);
            return false;
          }
          const age = calculateAge(student.date_of_birth);
          if (age < 13) {
            setError(`Student ${i + 1}: Must be at least 13 years old for password login. Students under 13 can use email login with parent guidance.`);
            return false;
          }
          if (!student.username.trim()) {
            setError(`Student ${i + 1}: Username is required`);
            return false;
          }
          if (student.username.length < 3) {
            setError(`Student ${i + 1}: Username must be at least 3 characters`);
            return false;
          }
          if (!student.password) {
            setError(`Student ${i + 1}: Password is required`);
            return false;
          }
          if (student.password.length < 8) {
            setError(`Student ${i + 1}: Password must be at least 8 characters`);
            return false;
          }
          if (student.password !== student.confirm_password) {
            setError(`Student ${i + 1}: Passwords do not match`);
            return false;
          }
        }
        if (student.program_ids.length === 0) {
          setError(`Student ${i + 1}: Please select at least one program`);
          return false;
        }
      }
    }

    if (step === 3) {
      // Check COPPA consent for minors
      const hasMinors = formData.students.some(
        s => s.date_of_birth && calculateAge(s.date_of_birth) < 13
      );
      if (hasMinors && !formData.coppa_consent_provided) {
        setError('You must provide parental consent for students under 13 (COPPA requirement)');
        return false;
      }

      if (!formData.liability_waiver_accepted) {
        setError('You must accept the Liability Waiver to continue');
        return false;
      }
      if (!formData.photo_video_consent) {
        setError('Please select a Photo/Video Release preference');
        return false;
      }
      if (!formData.digital_signature.trim()) {
        setError('Digital signature is required');
        return false;
      }
    }

    return true;
  };

  const nextStep = () => {
    if (validateStep()) {
      setStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    setStep(prev => prev - 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    setSubmitting(true);
    setError(null);

    try {
      // Prepare students data
      const studentsData = formData.students.map(student => ({
        name: student.name,
        date_of_birth: student.date_of_birth || null,
        age: student.date_of_birth ? calculateAge(student.date_of_birth) : null,
        email: student.email || null,
        current_belt: student.current_belt,
        program_ids: student.program_ids,
        username: student.enable_password ? student.username : null,
        password_hash: student.enable_password ? student.password : null, // Backend will hash
      }));

      const payload = {
        parent_name: formData.parent_name,
        parent_email: formData.parent_email.toLowerCase(),
        parent_phone: formData.parent_phone || null,
        parent_address: formData.parent_address || null,
        emergency_contact_name: formData.emergency_contact_name || null,
        emergency_contact_phone: formData.emergency_contact_phone || null,
        account_type: formData.account_type,
        students: studentsData,
        selected_program_ids: formData.selected_program_ids,
        payment_method_preference: formData.payment_method_preference || null,
        quickstart_interest: formData.quickstart_interest,
        liability_waiver_accepted: formData.liability_waiver_accepted,
        photo_video_consent: formData.photo_video_consent,
        photo_video_caption_preference: formData.photo_video_caption_preference || null,
        digital_signature: formData.digital_signature,
        coppa_consent_provided: formData.coppa_consent_provided,  // COPPA compliance
        ...(ref ? { resource_link_code: ref } : {}),
      };

      const response = await fetch(`${BACKEND_URL}/api/public/member-registration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.status === 429) {
        setError('Too many registration attempts. Please try again later.');
        return;
      }

      if (!response.ok) {
        setError(data.detail || 'Failed to submit registration. Please try again.');
        return;
      }

      // If the response includes a temporary access token, show preview option
      if (data.temporary_access_token) {
        setRegistrationSuccess({
          tempToken: data.temporary_access_token,
          resourceTitle: resourceLinkInfo?.title || 'the resource',
        });
        setStep(5); // Go to success step with preview option
        window.scrollTo(0, 0);
        return;
      }

      // Success - navigate to verification page
      navigate('/register/verify-pending', {
        state: { email: formData.parent_email }
      });

    } catch (err) {
      console.error('Registration error:', err);
      setError('An error occurred. Please try again or contact the studio at (352) 428-0086.');
    } finally {
      setSubmitting(false);
    }
  };

  // Step indicators - adjust based on mode
  const steps = isExistingCustomer
    ? [
        { num: 1, title: 'Login', icon: UserPlus },
        { num: 2, title: 'Students', icon: Users },
        { num: 3, title: 'Waivers', icon: FileCheck },
        { num: 4, title: 'Review', icon: CheckCircle },
      ]
    : [
        { num: 1, title: 'Parent Info', icon: UserPlus },
        { num: 2, title: 'Students', icon: Users },
        { num: 3, title: 'Waivers', icon: FileCheck },
        { num: 4, title: 'Review', icon: CheckCircle },
      ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Resource Link Banner */}
        {resourceLinkInfo && !registrationSuccess && (
          <Alert className="mb-6 bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700">
            <BookOpen className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 dark:text-amber-200">
              <span className="font-semibold">Register to access:</span>{' '}
              {resourceLinkInfo.title}
              {resourceLinkInfo.description && (
                <span className="block text-sm text-amber-700 dark:text-amber-300 mt-1">
                  {resourceLinkInfo.description}
                </span>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Registration Success with Preview */}
        {registrationSuccess && (
          <Card className="mb-6">
            <CardContent className="pt-6 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Registration Submitted</h2>
              <p className="text-gray-600 mb-4">
                Your registration has been submitted and is pending approval. A confirmation email has been sent to{' '}
                <strong>{formData.parent_email}</strong>.
              </p>
              <p className="text-gray-600 mb-6">
                While you wait, you can preview the resource you registered for.
              </p>
              <Button asChild className="bg-amber-600 hover:bg-amber-700 text-white">
                <a href={`/register/preview?token=${registrationSuccess.tempToken}`}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Preview {registrationSuccess.resourceTitle}
                </a>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Mode Selector Banner */}
        {!isExistingCustomer && !registrationSuccess && (
          <Alert className="mb-6 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
            <Info className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span className="text-sm">
                Already training at Owaza?{' '}
                <button
                  onClick={() => navigate('/register?mode=existing')}
                  className="text-blue-600 dark:text-blue-400 underline hover:text-blue-700 font-medium"
                >
                  Click here
                </button>
                {' '}for faster portal onboarding.
              </span>
            </AlertDescription>
          </Alert>
        )}

        {isExistingCustomer && !registrationSuccess && (
          <Alert className="mb-6 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
            <Info className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span className="text-sm">
                <strong>Existing Member:</strong> Create your portal account with streamlined onboarding.{' '}
                <button
                  onClick={() => navigate('/register?mode=new')}
                  className="text-green-600 dark:text-green-400 underline hover:text-green-700 font-medium"
                >
                  New to Owaza? Click here.
                </button>
              </span>
            </AlertDescription>
          </Alert>
        )}

        {/* Progress Indicator */}
        {!registrationSuccess && (<>
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((s, idx) => (
              <div key={s.num} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    step >= s.num
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-300 text-gray-600'
                  }`}>
                    {step > s.num ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <s.icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={`text-xs mt-1 font-medium ${
                    step >= s.num ? 'text-red-600' : 'text-gray-500'
                  }`}>
                    {s.title}
                  </span>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`h-1 flex-1 mx-2 ${
                    step > s.num ? 'bg-red-600' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Main Form Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl text-center">
              {step === 1 && (isExistingCustomer ? 'Create Your Portal Account' : 'Parent/Guardian Information')}
              {step === 2 && 'Student Information'}
              {step === 3 && 'Waivers & Consent'}
              {step === 4 && 'Review & Submit'}
            </CardTitle>
            <CardDescription className="text-center">
              {step === 1 && (isExistingCustomer ? 'Get online access to your training progress and resources' : 'Tell us about yourself')}
              {step === 2 && 'Add student(s) to this registration'}
              {step === 3 && 'Please review and accept the required waivers'}
              {step === 4 && 'Review your information before submitting'}
            </CardDescription>
            <p className="text-center text-sm">
              <a
                href={PRICE_LIST_PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 underline hover:text-red-700 font-medium"
              >
                View tuition &amp; fees (PDF)
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </p>
          </CardHeader>

          <CardContent className="p-6">
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Step 1: Parent Information */}
            {step === 1 && isExistingCustomer && (
              <div className="space-y-4">
                <Alert className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    <strong>Existing Owaza Member:</strong> You're creating a portal account for online access to training resources, progress tracking, and more. Enter your email to get started.
                  </AlertDescription>
                </Alert>

                <div>
                  <Label htmlFor="parent_email">
                    Your Email Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="parent_email"
                    type="email"
                    value={formData.parent_email}
                    onChange={(e) => updateFormData('parent_email', e.target.value)}
                    placeholder="john@example.com"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    We'll use this to verify your account and send login instructions
                  </p>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Since you're already training with us, we'll streamline this process. You won't need to re-enter information we already have on file (contact info, emergency contacts, etc.).
                  </p>
                </div>
              </div>
            )}

            {step === 1 && !isExistingCustomer && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="parent_name">
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="parent_name"
                    value={formData.parent_name}
                    onChange={(e) => updateFormData('parent_name', e.target.value)}
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <Label htmlFor="parent_email">
                    Email Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="parent_email"
                    type="email"
                    value={formData.parent_email}
                    onChange={(e) => updateFormData('parent_email', e.target.value)}
                    placeholder="john@example.com"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    We'll send verification and approval notifications to this email
                  </p>
                </div>

                {!isExistingCustomer && (
                  <>
                    <div>
                      <Label htmlFor="parent_phone">Phone Number</Label>
                      <Input
                        id="parent_phone"
                        type="tel"
                        value={formData.parent_phone}
                        onChange={(e) => updateFormData('parent_phone', e.target.value)}
                        placeholder="(555) 123-4567"
                      />
                    </div>

                    <div>
                      <Label htmlFor="parent_address">Address</Label>
                      <Textarea
                        id="parent_address"
                        value={formData.parent_address}
                        onChange={(e) => updateFormData('parent_address', e.target.value)}
                        placeholder="123 Main St, Spring Hill, FL 34610"
                        rows={2}
                      />
                    </div>

                    <div className="border-t pt-4 mt-6">
                      <h3 className="font-semibold mb-4">Emergency Contact</h3>

                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="emergency_contact_name">Contact Name</Label>
                          <Input
                            id="emergency_contact_name"
                            value={formData.emergency_contact_name}
                            onChange={(e) => updateFormData('emergency_contact_name', e.target.value)}
                            placeholder="Jane Smith"
                          />
                        </div>

                        <div>
                          <Label htmlFor="emergency_contact_phone">Contact Phone</Label>
                          <Input
                            id="emergency_contact_phone"
                            type="tel"
                            value={formData.emergency_contact_phone}
                            onChange={(e) => updateFormData('emergency_contact_phone', e.target.value)}
                            placeholder="(555) 987-6543"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="border-t pt-4 mt-6">
                      <Label>Account Type</Label>
                      <RadioGroup
                        value={formData.account_type}
                        onValueChange={(value) => updateFormData('account_type', value)}
                        className="mt-2"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="parent" id="type-parent" />
                          <Label htmlFor="type-parent" className="cursor-pointer font-normal">
                            Parent (managing children's accounts)
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="individual" id="type-individual" />
                          <Label htmlFor="type-individual" className="cursor-pointer font-normal">
                            Individual (adult student, single account)
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Step 2: Students */}
            {step === 2 && (
              <div className="space-y-6">
                {formData.students.map((student, idx) => (
                  <Card key={idx} className="border-2">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="text-lg">
                        Student {idx + 1}
                      </CardTitle>
                      {formData.students.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeStudent(idx)}
                          className="text-red-600 hover:text-red-700"
                        >
                          Remove
                        </Button>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label>
                          Student Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={student.name}
                          onChange={(e) => updateStudent(idx, 'name', e.target.value)}
                          placeholder="Johnny Smith"
                        />
                      </div>

                      <div>
                        <Label>Date of Birth</Label>
                        <Input
                          type="date"
                          value={student.date_of_birth}
                          onChange={(e) => updateStudent(idx, 'date_of_birth', e.target.value)}
                        />
                        {student.date_of_birth && (
                          <p className="text-xs text-gray-500 mt-1">
                            Age: {calculateAge(student.date_of_birth)} years old
                          </p>
                        )}
                      </div>

                      <div>
                        <Label>Student Email (optional)</Label>
                        <Input
                          type="email"
                          value={student.email}
                          onChange={(e) => updateStudent(idx, 'email', e.target.value)}
                          placeholder="johnny@example.com"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          For independent login via email (magic link/OTP)
                        </p>
                      </div>

                      <div>
                        <Label>
                          Current Belt Rank <span className="text-red-500">*</span>
                        </Label>
                        <select
                          className="w-full border rounded-md p-2"
                          value={student.current_belt}
                          onChange={(e) => updateStudent(idx, 'current_belt', e.target.value)}
                        >
                          <option value="white">White Belt</option>
                          <option value="yellow">Yellow Belt</option>
                          <option value="orange">Orange Belt</option>
                          <option value="green">Green Belt</option>
                          <option value="blue">Blue Belt</option>
                          <option value="purple">Purple Belt</option>
                          <option value="brown">Brown Belt</option>
                          <option value="black_1">Black Belt (1st Dan)</option>
                        </select>
                      </div>

                      <div>
                        <Label>
                          Programs Interested In <span className="text-red-500">*</span>
                        </Label>
                        {loadingPrograms ? (
                          <p className="text-sm text-gray-500">Loading programs...</p>
                        ) : (
                          <div className="space-y-2 mt-2">
                            {programs.map(program => (
                              <div key={program.id} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`student-${idx}-program-${program.id}`}
                                  checked={student.program_ids.includes(program.id)}
                                  onCheckedChange={(checked) => {
                                    const newPrograms = checked
                                      ? [...student.program_ids, program.id]
                                      : student.program_ids.filter(id => id !== program.id);
                                    updateStudent(idx, 'program_ids', newPrograms);
                                  }}
                                />
                                <Label
                                  htmlFor={`student-${idx}-program-${program.id}`}
                                  className="cursor-pointer font-normal"
                                >
                                  {program.title}
                                </Label>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Password Login Section */}
                      <div className="border-t pt-4">
                        <div className="flex items-center space-x-2 mb-4">
                          <Checkbox
                            id={`student-${idx}-enable-password`}
                            checked={student.enable_password}
                            onCheckedChange={(checked) => updateStudent(idx, 'enable_password', checked)}
                            disabled={student.date_of_birth && calculateAge(student.date_of_birth) < 13}
                          />
                          <Label
                            htmlFor={`student-${idx}-enable-password`}
                            className="cursor-pointer font-normal"
                          >
                            Enable username/password login (Age 13+ only)
                          </Label>
                        </div>

                        {student.date_of_birth && calculateAge(student.date_of_birth) < 13 && (
                          <Alert className="mb-4">
                            <AlertDescription className="text-sm">
                              Students under 13 can use email-based login (magic link or OTP code) with parent guidance.
                            </AlertDescription>
                          </Alert>
                        )}

                        {student.enable_password && (
                          <div className="space-y-4 pl-6">
                            <div>
                              <Label>Username <span className="text-red-500">*</span></Label>
                              <Input
                                value={student.username}
                                onChange={(e) => updateStudent(idx, 'username', e.target.value)}
                                placeholder="johnnysmith"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Minimum 3 characters, lowercase letters and numbers only
                              </p>
                            </div>

                            <div>
                              <Label>Password <span className="text-red-500">*</span></Label>
                              <Input
                                type="password"
                                value={student.password}
                                onChange={(e) => updateStudent(idx, 'password', e.target.value)}
                                placeholder="••••••••"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Minimum 8 characters
                              </p>
                            </div>

                            <div>
                              <Label>Confirm Password <span className="text-red-500">*</span></Label>
                              <Input
                                type="password"
                                value={student.confirm_password}
                                onChange={(e) => updateStudent(idx, 'confirm_password', e.target.value)}
                                placeholder="••••••••"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <Button
                  variant="outline"
                  onClick={addStudent}
                  className="w-full"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Add Another Student
                </Button>
              </div>
            )}

            {/* Step 3: Waivers & Consent */}
            {step === 3 && (
              <div className="space-y-6">
                {/* COPPA Consent - Show only if any student is under 13 */}
                {formData.students.some(s => s.date_of_birth && calculateAge(s.date_of_birth) < 13) && (
                  <div className="border-2 border-amber-400 rounded-lg p-4 bg-amber-50 dark:bg-amber-900/10">
                    <h3 className="font-semibold text-lg mb-3 text-amber-900 dark:text-amber-100">
                      Children's Privacy Notice (COPPA)
                    </h3>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                      We are required by law to obtain verifiable parental consent before collecting
                      personal information from children under 13 years of age. By checking the box
                      below, you certify that you are the parent or legal guardian of the student(s)
                      under 13 listed in this registration.
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                      We collect the following information from children under 13:
                    </p>
                    <ul className="text-sm text-gray-700 dark:text-gray-300 list-disc pl-5 space-y-1 mb-4">
                      <li>Name, date of birth, and email (optional)</li>
                      <li>Attendance records and training progress</li>
                      <li>Quiz/learning activity results</li>
                      <li>Profile photos (if uploaded)</li>
                    </ul>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                      This information is used solely to provide martial arts training services,
                      track progress, and communicate with parents. We do not share children's
                      information with third parties for marketing purposes. For more details,
                      see our{' '}
                      <a
                        href="/page/safety-and-privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-red-600 dark:text-red-400 underline hover:text-red-700"
                      >
                        Children's Privacy Policy
                      </a>.
                    </p>
                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="coppa-consent"
                        checked={formData.coppa_consent_provided}
                        onCheckedChange={(checked) => updateFormData('coppa_consent_provided', checked)}
                      />
                      <Label htmlFor="coppa-consent" className="cursor-pointer font-normal">
                        I am the parent/legal guardian of all students under 13 listed above, and I
                        consent to the collection and use of their personal information as described
                        in the Children's Privacy Policy.{' '}
                        <span className="text-red-500">*</span>
                      </Label>
                    </div>
                  </div>
                )}

                {/* Liability Waiver */}
                <div>
                  <h3 className="font-semibold text-lg mb-3">Liability Waiver and Release</h3>
                  <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800 max-h-64 overflow-y-auto mb-4">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                      By signing below, you acknowledge and agree to the terms of the Owaza Martial Arts Liability Waiver and Release of Responsibility. This waiver includes, but is not limited to:
                    </p>
                    <ul className="text-sm text-gray-700 dark:text-gray-300 list-disc pl-5 space-y-1 mb-3">
                      <li>Release from liability for any injuries sustained during training</li>
                      <li>Acknowledgment of the physical nature and inherent risks of martial arts training</li>
                      <li>Agreement to follow all safety rules and instructor guidance</li>
                      <li>Confirmation of adequate health and physical condition to participate</li>
                    </ul>
                    <a
                      href="/documents/liability-waiver.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-red-600 dark:text-red-400 text-sm underline hover:text-red-700"
                    >
                      Download Full Liability Waiver (PDF)
                    </a>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="liability-waiver"
                      checked={formData.liability_waiver_accepted}
                      onCheckedChange={(checked) => updateFormData('liability_waiver_accepted', checked)}
                    />
                    <Label htmlFor="liability-waiver" className="cursor-pointer font-normal">
                      I have read and agree to the Liability Waiver and Release <span className="text-red-500">*</span>
                    </Label>
                  </div>
                </div>

                {/* Photo/Video Release */}
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-lg mb-3">Photo & Video Release</h3>
                  <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800 max-h-64 overflow-y-auto mb-4">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                      Owaza Martial Arts may photograph, film, and/or record students during classes, events, and demonstrations for:
                    </p>
                    <ul className="text-sm text-gray-700 dark:text-gray-300 list-disc pl-5 space-y-1 mb-3">
                      <li>Social media posts and promotional materials</li>
                      <li>Website content and marketing</li>
                      <li>Studio displays and archives</li>
                      <li>Educational and training purposes</li>
                    </ul>
                    <a
                      href="/documents/photo-video-release.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-red-600 dark:text-red-400 text-sm underline hover:text-red-700"
                    >
                      Download Full Photo/Video Release (PDF)
                    </a>
                  </div>
                  <RadioGroup
                    value={formData.photo_video_consent}
                    onValueChange={(value) => updateFormData('photo_video_consent', value)}
                  >
                    <div className="flex items-center space-x-2 mb-2">
                      <RadioGroupItem value="consent" id="photo-consent" />
                      <Label htmlFor="photo-consent" className="cursor-pointer font-normal">
                        I CONSENT to the use of images/video <span className="text-red-500">*</span>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="do_not_consent" id="photo-no-consent" />
                      <Label htmlFor="photo-no-consent" className="cursor-pointer font-normal">
                        I DO NOT CONSENT to the use of images/video <span className="text-red-500">*</span>
                      </Label>
                    </div>
                  </RadioGroup>

                  {formData.photo_video_consent === 'consent' && (
                    <div className="mt-4 pl-6">
                      <Label htmlFor="caption-preference">Caption Preference (Optional)</Label>
                      <Input
                        id="caption-preference"
                        value={formData.photo_video_caption_preference}
                        onChange={(e) => updateFormData('photo_video_caption_preference', e.target.value)}
                        placeholder="e.g., Johnny S."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        How would you like your student(s) to be identified in captions? (First name + last initial, initials only, etc.)
                      </p>
                    </div>
                  )}
                </div>

                {/* Digital Signature */}
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-lg mb-3">Digital Signature</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    By typing your name below, you electronically sign this agreement and certify that all information provided is accurate. You confirm that you are the parent or legal guardian of the minor student(s) listed above (if applicable) and have the authority to agree to these terms.
                  </p>
                  <div>
                    <Label htmlFor="digital-signature">
                      Type Your Full Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="digital-signature"
                      value={formData.digital_signature}
                      onChange={(e) => updateFormData('digital_signature', e.target.value)}
                      placeholder="John Smith"
                      className="font-serif text-lg"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Signed on: {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Review & Submit */}
            {step === 4 && (
              <div className="space-y-6">
                {/* Parent Info Summary */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-lg">Parent/Guardian Information</h3>
                    <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="text-red-600 hover:text-red-700">
                      Edit
                    </Button>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-sm">
                    <p className="mb-1"><strong>Name:</strong> {formData.parent_name}</p>
                    <p className="mb-1"><strong>Email:</strong> {formData.parent_email}</p>
                    {formData.parent_phone && <p className="mb-1"><strong>Phone:</strong> {formData.parent_phone}</p>}
                    {formData.parent_address && <p className="mb-1"><strong>Address:</strong> {formData.parent_address}</p>}
                    {formData.emergency_contact_name && (
                      <p className="mb-1"><strong>Emergency Contact:</strong> {formData.emergency_contact_name} - {formData.emergency_contact_phone}</p>
                    )}
                    <p className="mb-1"><strong>Account Type:</strong> {formData.account_type === 'parent' ? 'Parent (managing children)' : 'Individual'}</p>
                  </div>
                </div>

                {/* Students Summary */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-lg">Students ({formData.students.length})</h3>
                    <Button variant="ghost" size="sm" onClick={() => setStep(2)} className="text-red-600 hover:text-red-700">
                      Edit
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {formData.students.map((student, idx) => (
                      <div key={idx} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-sm">
                        <p className="font-semibold mb-1">{student.name}</p>
                        {student.date_of_birth && <p className="mb-1">Age: {calculateAge(student.date_of_birth)} years old</p>}
                        <p className="mb-1">Current Belt: {student.current_belt}</p>
                        <p className="mb-1">Programs: {student.program_ids.length} selected</p>
                        {student.enable_password && student.username && (
                          <p className="mb-1">Username: {student.username}</p>
                        )}
                        {student.email && <p className="mb-1">Email: {student.email}</p>}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Waivers Summary */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-lg">Waivers & Consent</h3>
                    <Button variant="ghost" size="sm" onClick={() => setStep(3)} className="text-red-600 hover:text-red-700">
                      Edit
                    </Button>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-sm space-y-1">
                    <p><CheckCircle className="w-4 h-4 inline mr-1 text-green-600" /> Liability Waiver Accepted</p>
                    <p><CheckCircle className="w-4 h-4 inline mr-1 text-green-600" /> Photo/Video: {formData.photo_video_consent === 'consent' ? 'Consent' : 'Do Not Consent'}</p>
                    {formData.photo_video_consent === 'consent' && formData.photo_video_caption_preference && (
                      <p className="ml-5">Caption preference: {formData.photo_video_caption_preference}</p>
                    )}
                    <p><CheckCircle className="w-4 h-4 inline mr-1 text-green-600" /> Signed by: {formData.digital_signature}</p>
                  </div>
                </div>

                <Alert className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                  <AlertDescription className="text-sm">
                    After submitting, you'll receive a verification email at <strong>{formData.parent_email}</strong>. Click the link in the email to verify your address, then our team will review your registration (usually within 24-48 hours). You'll receive a welcome email once approved with login instructions for the member portal.
                  </AlertDescription>
                </Alert>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              {step > 1 && (
                <Button variant="outline" onClick={prevStep}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              )}

              {step < 4 ? (
                <Button onClick={nextStep} className="ml-auto bg-red-600 hover:bg-red-700">
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="ml-auto bg-red-600 hover:bg-red-700"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Registration'
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        </>)}
      </div>
    </div>
  );
};

export default MemberRegistrationForm;
